import { useState } from "react";
import { Link } from "react-router-dom";
import { Message } from "primereact/message";
import { BenefitsMetricsRefreshingIndicator } from "../../Reporting/components/BenefitsMetricsWidgets";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { AppDialog, StatusBadge } from "../../../components/ui";
import { Attendance, AttendanceHistoryItem } from "../../../models/attendance";
import { useGetUserAttendanceHistoryQuery } from "../../../store/services/attendances";
import { PersonCell } from "./PersonCell";
import { formatDateWithoutSeconds } from "../../../utils/functions/date";

interface AttendanceHistoryDialogProps {
  attendance: Attendance | null;
  tenantId: string | null;
  onHide: () => void;
}

const HISTORY_PER_PAGE = 10;
export const AttendanceHistoryDialog = ({
  attendance,
  tenantId,
  onHide,
}: AttendanceHistoryDialogProps) => {
  const [page, setPage] = useState(1);
  const personUserId = attendance?.personUserId ?? null;

  const { currentData: data, isLoading, isFetching, isError } = useGetUserAttendanceHistoryQuery(
    { userId: personUserId ?? 0, tenantId, page, perPage: HISTORY_PER_PAGE },
    { skip: personUserId === null }
  );

  const totalCount = data?.total;
  const presentCount = data?.presentCount;
  const absentCount = data?.absentCount;

  const personName = data?.person.name ?? attendance?.personName ?? "";
  const personEmail = data?.person.email ?? attendance?.personEmail ?? "";
  const personAvatar = data?.person.avatarUrl ?? attendance?.personAvatarUrl ?? null;

  const handleHide = () => {
    setPage(1);
    onHide();
  };

  return (
    <AppDialog
      variant="detail"
      visible={attendance !== null}
      onHide={handleHide}
      header={`Historial de Asistencia — ${personName}`}
      className="attendances-ui"
      style={{ width: "600px", maxWidth: "95vw" }}
    >
      <div className="mb-4">
        <PersonCell
          name={personName}
          avatarUrl={personAvatar}
          secondaryLine={personEmail || "—"}
          size={44}
        />
      </div>

      <div className="attendances-history-stats mb-4">
        <div className="attendances-stat">
          <span className="attendances-stat__label">Total Eventos:</span>
          <span className="attendances-stat__value">{totalCount ?? "—"}</span>
        </div>
        <div className="attendances-stat attendances-stat--present">
          <span className="attendances-stat__label">Presentes:</span>
          <span className="attendances-stat__value">{presentCount ?? "—"}</span>
        </div>
        <div className="attendances-stat attendances-stat--absent">
          <span className="attendances-stat__label">Ausentes:</span>
          <span className="attendances-stat__value">
            {absentCount ?? "—"}
          </span>
        </div>
      </div>

      <BenefitsMetricsRefreshingIndicator visible={isFetching && Boolean(data)} />
      {isError && <Message severity="error" text="No se pudo cargar el historial de asistencia." />}
      <DataTable
        value={data?.items ?? []}
        loading={isLoading || (isFetching && !data)}
        dataKey="id"
        lazy
        paginator
        rows={HISTORY_PER_PAGE}
        first={(page - 1) * HISTORY_PER_PAGE}
        totalRecords={data?.total ?? 0}
        onPage={(event: DataTablePageEvent) => setPage((event.page ?? 0) + 1)}
        responsiveLayout="scroll"
        emptyMessage="No hay asistencias todavía"
      >
        <Column
          header="Evento"
          body={(row: AttendanceHistoryItem) => {
            const subtitle = [row.eventType, row.eventSubtype].filter(Boolean).join(" · ");
            return (
              <div className="flex flex-column">
                <Link to={`/events/${row.eventId}`} className="font-medium">
                  {row.eventName}
                </Link>
                {subtitle && <span className="attendances-cell-sub text-600">{subtitle}</span>}
              </div>
            );
          }}
        />
        <Column
          header="Fecha"
          body={(row: AttendanceHistoryItem) => formatDateWithoutSeconds(row.eventDate)}
          style={{ width: "8rem" }}
        />
        <Column
          header="Host"
          body={(row: AttendanceHistoryItem) => (
            <PersonCell name={row.hostName} avatarUrl={row.hostAvatarUrl} />
          )}
          style={{ width: "11rem" }}
        />
        <Column
          header="Estado"
          body={(row: AttendanceHistoryItem) => (
            <StatusBadge
              label={row.isPresent ? "Presente" : "Ausente"}
              tone={row.isPresent ? "success" : "error"}
            />
          )}
          style={{ width: "8rem" }}
        />
      </DataTable>
    </AppDialog>
  );
};
